import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../config/firebaseConfig";
import { registerInquiry } from "../data/ccProgram";
import "../styles/InquiryRegistration.css";

const StableInput = React.memo(function StableInput({
  value = "",
  onCommit,
  liveOnChange,
  hasError,
  className,
  ...props
}) {
  const ref = useRef(null);
  const local = useRef(value);

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.value = value;
      local.current = value;
    }
  }, [value]);

  const handleChange = useCallback((e) => {
    local.current = e.target.value;
    if (liveOnChange) liveOnChange(e.target.value);
  }, [liveOnChange]);

  const handleBlur = useCallback(() => {
    if (onCommit) onCommit(local.current);
  }, [onCommit]);

  return (
    <input
      ref={ref}
      defaultValue={value}
      onChange={handleChange}
      onBlur={handleBlur}
      className={`inq-input${hasError ? " inq-input-err" : ""}${className ? " " + className : ""}`}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      {...props}
    />
  );
});

const StableTextarea = React.memo(function StableTextarea({
  value = "",
  onCommit,
  hasError,
  ...props
}) {
  const ref = useRef(null);
  const local = useRef(value);

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.value = value;
      local.current = value;
    }
  }, [value]);

  return (
    <textarea
      ref={ref}
      defaultValue={value}
      onChange={e => { local.current = e.target.value; }}
      onBlur={() => { if (onCommit) onCommit(local.current); }}
      className={`inq-input inq-textarea${hasError ? " inq-input-err" : ""}`}
      autoComplete="off"
      spellCheck={false}
      {...props}
    />
  );
});

const Field = React.memo(function Field({ label, error, optional, children }) {
  return (
    <div className="inq-field-group">
      <label className="inq-label">
        {label}{optional && <span className="inq-opt"> optional</span>}
      </label>
      {children}
      {error && <div className="inq-error">{error}</div>}
    </div>
  );
});

const StableSelect = React.memo(function StableSelect({ value, onChange, hasError, children }) {
  return (
    <select
      className={`inq-input inq-select${hasError ? " inq-input-err" : ""}`}
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
    >
      {children}
    </select>
  );
});

const ReviewSection = React.memo(function ReviewSection({ title, icon, editStep, fields, onEdit }) {
  return (
    <div className="inq-rv-section">
      <div className="inq-rv-section-header">
        <span className="inq-rv-section-title">{icon} {title}</span>
        <button className="inq-rv-edit-btn" onClick={() => onEdit(editStep)}>✏️ Edit</button>
      </div>
      <div className="inq-rv-fields">
        {fields.map(f => (
          <div className="inq-rv-field" key={f.label}>
            <span className="inq-rv-label">{f.label}</span>
            <span className="inq-rv-value">{f.value || <em className="inq-rv-empty">—</em>}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

const PROGRAMS = [
  { value: "diabetes",     label: "Diabetes" },
  { value: "test_program", label: "Test Programme" },
];

const INIT = {
  country_code: "91",
  phone_no: "", name: "", email: "", city: "",
  age: "", gender: "",
  height: "", weight: "", diab_duration: "", medications: "", hba1c: "", conditions: [],
  risks: { insulin: false, hypoglycemia: false, kidney: false, complications: false },
  activity: "", sleep_duration: "", sleep_quality: "", eating_pattern: "",
  waist: "", hips: "", chest: "", wrist: "",
  food_likes: "", food_dislikes: "", meal_timing: "",
  program_id: "diabetes", doctor_name: "",
};

function calcAll(f) {
  const H = parseFloat(f.height), W = parseFloat(f.weight),
        A = parseInt(f.age),      G = f.gender, act = f.activity;
  if (!H || !W || !A) return {};
  const hm = H / 100;
  const bmi = +(W / (hm * hm)).toFixed(1);
  const bmi_category = bmi < 18.5 ? "Underweight" : bmi < 23 ? "Normal"
    : bmi < 25 ? "Overweight (Asian)" : bmi < 30 ? "Obese I" : "Obese II+";
  const bmr  = G === "male" ? 10*W + 6.25*H - 5*A + 5 : 10*W + 6.25*H - 5*A - 161;
  const am   = { sedentary: 1.2, moderate: 1.55, active: 1.725 };
  const tdee = Math.round(bmr * (am[act] || 1.2));
  const def  = bmi >= 30 ? 700 : bmi >= 25 ? 500 : bmi >= 23 ? 300 : 0;
  const target_calories = tdee - def;
  const protein_g = Math.round(W * 1.8);
  const fCal  = Math.round(target_calories * .28);
  const cCal  = target_calories - protein_g * 4 - fCal;
  const fat_g  = Math.round(fCal / 9);
  const carbs_g = Math.round(Math.max(0, cCal) / 4);
  const bf    = G === "male" ? bmi * 1.2 + A * .23 - 16.2 : bmi * 1.2 + A * .23 - 5.4;
  const body_category = G === "male"
    ? (bf < 15 ? "Low Fat" : bf < 25 ? "Moderate" : "High Fat")
    : (bf < 24 ? "Low Fat" : bf < 32 ? "Moderate" : "High Fat");
  const goal = bmi < 22 && body_category === "Low Fat" ? "Maintenance"
    : bmi < 25 ? "Recomposition" : "Fat Loss";
  return { bmi, bmi_category, target_calories, protein_g, carbs_g, fat_g, goal, body_category };
}

export default function InquiryRegistration() {
  const navigate = useNavigate();

  const [otpStep,       setOtpStep]       = useState("phone");
  const [otpCode,       setOtpCode]       = useState("");
  const [confirmResult, setConfirmResult] = useState(null);
  const [otpError,      setOtpError]      = useState("");
  const [otpSending,    setOtpSending]    = useState(false);
  const [otpVerifying,  setOtpVerifying]  = useState(false);

  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError,   setConsentError]   = useState("");

  const [screen,     setScreen]     = useState(0);
  const [form,       setForm]       = useState(INIT);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr,  setSubmitErr]  = useState(null);
  const [refNum,     setRefNum]     = useState("");
  const [submitResult, setSubmitResult] = useState(null);

  const set = useCallback((key, val) =>
    setForm(f => ({ ...f, [key]: val })), []);
  const setRisk = useCallback((key, val) =>
    setForm(f => ({ ...f, risks: { ...f.risks, [key]: val } })), []);

  const recaptchaInitialized = useRef(false);
  const recaptchaContainerRef = useRef(null);

  useEffect(() => {
    if (recaptchaInitialized.current) return;
    recaptchaInitialized.current = true;
    try {
      window._recaptchaVerifier = new RecaptchaVerifier(
        auth, "recaptcha-container",
        {
          size: "invisible", callback: () => {},
          "expired-callback": () => {
            window._recaptchaVerifier = null;
            recaptchaInitialized.current = false;
          },
        }
      );
      window._recaptchaVerifier.render().catch(e => console.error("reCAPTCHA:", e));
    } catch (e) {
      console.error("reCAPTCHA init:", e);
      recaptchaInitialized.current = false;
    }
    return () => {
      if (window._recaptchaVerifier) {
        try { window._recaptchaVerifier.clear(); } catch (_) {}
        window._recaptchaVerifier = null;
      }
      recaptchaInitialized.current = false;
      if (recaptchaContainerRef.current) recaptchaContainerRef.current.innerHTML = "";
    };
  }, []);

  const goTo = useCallback((n) => {
    setScreen(n);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const sendOtp = async () => {
    setOtpError("");
    const phone = form.phone_no.trim().replace(/\s/g, "");
    if (!phone || phone.length < 8) { setOtpError("Please enter a valid mobile number."); return; }
    if (!window._recaptchaVerifier) { setOtpError("reCAPTCHA not ready. Please refresh."); return; }
    setOtpSending(true);
    try {
      const result = await signInWithPhoneNumber(auth, `+${form.country_code}${phone}`, window._recaptchaVerifier);
      setConfirmResult(result); setOtpStep("otp");
    } catch (err) {
      setOtpError(err.message || "Failed to send OTP.");
      if (window._recaptchaVerifier) {
        try { window._recaptchaVerifier.clear(); } catch (_) {}
        window._recaptchaVerifier = null; recaptchaInitialized.current = false;
      }
    } finally { setOtpSending(false); }
  };

  const verifyOtp = async () => {
    setOtpError("");
    if (!otpCode || otpCode.length < 4) { setOtpError("Please enter the OTP."); return; }
    setOtpVerifying(true);
    try {
      await confirmResult.confirm(otpCode);
      setOtpStep("done"); goTo(0.5);
    } catch { setOtpError("Invalid OTP. Please try again."); }
    finally { setOtpVerifying(false); }
  };

  const continueFromConsent = () => {
    if (!consentChecked) { setConsentError("Patient must tick the consent box to proceed."); return; }
    setConsentError(""); goTo(1);
  };

  function validate() {
    const e = {};
    if (screen === 1) {
      if (!form.name.trim())         e.name        = "Please enter full name";
      if (!form.email.includes("@")) e.email       = "Valid email required";
      if (!form.city.trim())         e.city        = "Please enter city";
      if (!form.doctor_name.trim())  e.doctor_name = "Please enter doctor name";
    }
    if (screen === 2) {
      const a = parseInt(form.age);
      if (!a || a < 18 || a > 90) e.age    = "Age must be 18–90";
      if (!form.gender)            e.gender = "Please select gender";
    }
    if (screen === 3) {
      const h = parseFloat(form.height), w = parseFloat(form.weight);
      if (!h || h < 100 || h > 250) e.height        = "Enter valid height (100–250 cm)";
      if (!w || w < 30  || w > 300) e.weight        = "Enter valid weight (30–300 kg)";
      if (!form.diab_duration)      e.diab_duration = "Please select duration";
      if (!form.conditions || form.conditions.length === 0)
      e.conditions = "Please select at least one option";
    }
    if (screen === 5) { if (!form.activity) e.activity = "Please select activity level"; }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (!validate()) return;
    if (screen === 8) { goTo(9); return; }
    goTo(screen + 1);
  }

  function back() {
    if (screen <= 1) { goTo(0.5); return; }
    goTo(screen - 1);
  }

  async function submitForm() {
    const calc = calcAll(form);
    setSubmitting(true); setSubmitErr(null);
    const payload = {
      country_code: form.country_code,
      phone_no: form.phone_no.trim().replace(/\s/g, ""),
      name: form.name.trim(), email: form.email.trim(), city: form.city.trim(),
      age: parseInt(form.age), gender: form.gender,
      height: parseFloat(form.height), weight: parseFloat(form.weight),
      diab_duration: form.diab_duration, medications: form.medications || null,
      hba1c: parseFloat(form.hba1c) || null,
      risk_insulin: form.risks.insulin, risk_hypoglycemia: form.risks.hypoglycemia,
      risk_kidney: form.risks.kidney, risk_complications: form.risks.complications,
      activity: form.activity, sleep_duration: form.sleep_duration,
      sleep_quality: form.sleep_quality, eating_pattern: form.eating_pattern,
      waist: parseFloat(form.waist) || null, hips: parseFloat(form.hips) || null,
      chest: parseFloat(form.chest) || null, wrist: parseFloat(form.wrist) || null,
      food_likes: form.food_likes || null, food_dislikes: form.food_dislikes || null,
      meal_timing: form.meal_timing || null,
      bmi: calc.bmi, bmi_category: calc.bmi_category,
      target_calories: calc.target_calories, protein_g: calc.protein_g,
      carbs_g: calc.carbs_g, fat_g: calc.fat_g,
      goal: calc.goal, body_category: calc.body_category,
      condition: form.conditions.join(", "),
      program_id: form.program_id, doctor_name: form.doctor_name.trim(),
    };
    try {
      const res = await registerInquiry(payload);
      const data = res?.data;
      setSubmitResult(data);
      const ref = data?.patient_id
        ? `FH-${data.patient_id.slice(-6).toUpperCase()}`
        : `FH-${Date.now().toString(36).toUpperCase().slice(-6)}`;
      setRefNum(ref);
      goTo(10);
    } catch (err) {
      setSubmitErr(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const isFormStep = screen >= 1 && screen <= 8;
  const pct = isFormStep ? Math.round((screen / 8) * 100) : 0;
  const calc = useMemo(() => calcAll(form), [form]);

  return (
    <div className="inq-root" style={{ width: "100%", maxWidth: 760, margin: "0 auto" }}>
      <div className="inq-bg">
        <div className="inq-bg-blob inq-blob-1" />
        <div className="inq-bg-blob inq-blob-2" />
        <div className="inq-bg-grid" />
      </div>

      <div className="inq-top-bar">
        <button className="inq-back-home" onClick={() => navigate("/care-coordinator")}>
          ← Back to Home
        </button>
      </div>

      <div id="recaptcha-container" ref={recaptchaContainerRef} />

      {isFormStep && (
        <div className="inq-progress-bar">
          <div className="inq-progress-fill" style={{ width: pct + "%" }} />
        </div>
      )}

      <div className="inq-app">

        {isFormStep && (
          <div className="inq-step-header">
            <span className="inq-step-label">Step {screen} of 8</span>
            <div className="inq-dots">
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} className={`inq-dot${i+1===screen?" active":i+1<screen?" done":""}`} />
              ))}
            </div>
          </div>
        )}

        {screen === 0 && (
          <div className="inq-hero">
            <div className="inq-hero-badge"><span className="inq-hero-pulse" /> New Inquiry</div>
            <h1 className="inq-hero-title">Register a <em>New Patient</em><br />Inquiry</h1>
            <p className="inq-hero-sub">
              Verify the patient's phone number with OTP, then collect their health profile
              to create a personalised diabetes management plan.
            </p>
            <div className="inq-pills">
              <div className="inq-pill">📱 Phone Verified</div>
              <div className="inq-pill">🩺 Doctor-led</div>
              <div className="inq-pill">🔒 Secure</div>
            </div>

            <div className="inq-otp-box">
              {otpStep === "phone" && (
                <>
                  <p className="inq-otp-title">Step 1: Verify Patient's Phone Number</p>
                  {otpError && <div className="inq-error" style={{ marginBottom: 12 }}>{otpError}</div>}
                  <div className="inq-phone-row">
                    <div className="inq-country-code">+{form.country_code}</div>
                    <StableInput
                      className="inq-phone-input"
                      placeholder="98765 43210"
                      type="tel"
                      value={form.phone_no}
                      liveOnChange={val => set("phone_no", val)}
                      onCommit={val => set("phone_no", val)}
                      onKeyDown={e => e.key === "Enter" && sendOtp()}
                      autoComplete="tel"
                    />
                  </div>
                  <button className="inq-btn-primary" onClick={sendOtp} disabled={otpSending}>
                    {otpSending ? "Sending OTP…" : "Send OTP"}
                    {!otpSending && <span className="inq-arrow">→</span>}
                  </button>
                </>
              )}
              {otpStep === "otp" && (
                <>
                  <p className="inq-otp-title">OTP sent to +{form.country_code} {form.phone_no}</p>
                  {otpError && <div className="inq-error" style={{ marginBottom: 12 }}>{otpError}</div>}
                  <input
                    className="inq-input inq-otp-input"
                    placeholder="• • • • • •"
                    type="number"
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && verifyOtp()}
                    autoFocus
                  />
                  <div className="inq-otp-actions">
                    <button className="inq-btn-primary" onClick={verifyOtp} disabled={otpVerifying}>
                      {otpVerifying ? "Verifying…" : "Verify & Continue"}
                      {!otpVerifying && <span className="inq-arrow">→</span>}
                    </button>
                    <button className="inq-btn-secondary" onClick={() => { setOtpStep("phone"); setOtpCode(""); setOtpError(""); }}>
                      ← Change number
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {screen === 0.5 && (
          <div className="inq-screen">
            <div className="inq-consent-icon">🛡️</div>
            <h2 className="inq-screen-title">Patient Consent</h2>
            <p className="inq-screen-sub">
              Before we proceed, the patient must acknowledge and agree to the following declaration.
            </p>
            <div className="inq-consent-box">
              <div className="inq-consent-header">
                <span className="inq-consent-badge">Declaration of Consent</span>
                <span className="inq-consent-badge-sub">Please read carefully</span>
              </div>
              <div className="inq-consent-body">
                <p className="inq-consent-intro">I, the patient, hereby voluntarily consent to the following:</p>
                {[
                  { n: "01", title: "Accuracy of Information",
                    body: "All personal, health, and lifestyle information I provide during this registration is true, accurate, and complete to the best of my knowledge." },
                  { n: "02", title: "Data Usage",
                    body: "I agree that my health data will be used by FutureHealth to create a personalised NCD management plan and may be shared with my assigned doctor and care team." },
                  { n: "03", title: "Programme Participation",
                    body: "I understand that this assessment and programme recommendations are supplementary to, and not a replacement for, advice from a licensed medical professional." },
                  { n: "04", title: "Privacy & Security",
                    body: "My data will be stored securely and will not be sold or shared with third parties outside the care team without my explicit written permission." },
                ].map(item => (
                  <div className="inq-consent-item" key={item.n}>
                    <span className="inq-consent-num">{item.n}</span>
                    <div><strong>{item.title}</strong><p>{item.body}</p></div>
                  </div>
                ))}
              </div>
              <label
                className="inq-consent-check-row"
                onClick={() => { setConsentChecked(c => !c); setConsentError(""); }}
              >
                <div className={`inq-consent-checkbox${consentChecked ? " checked" : ""}`}>
                  {consentChecked && <span className="inq-consent-tick">✓</span>}
                </div>
                <span className="inq-consent-check-label">
                  I have read and understood the above. I confirm that all information I provide will be truthful and accurate.
                </span>
              </label>
              {consentError && <div className="inq-error" style={{ marginTop: 10, padding: "0 20px 16px" }}>{consentError}</div>}
            </div>
            <button className="inq-btn-primary" style={{ marginTop: 24 }} onClick={continueFromConsent}>
              I Agree & Continue <span className="inq-arrow">→</span>
            </button>
            <button className="inq-btn-secondary" style={{ marginTop: 12 }}
              onClick={() => { setOtpStep("phone"); setOtpCode(""); goTo(0); }}>
              ← Go back
            </button>
          </div>
        )}

        {screen === 1 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Contact & Programme</h2>
            <p className="inq-screen-sub">Basic details and programme assignment.</p>
            <div className="inq-otp-verified-badge">
              ✅ Verified: +{form.country_code} {form.phone_no}
            </div>

            <Field label="Full Name" error={errors.name}>
              <StableInput
                value={form.name} hasError={!!errors.name}
                onCommit={v => set("name", v)}
                type="text" placeholder="e.g. Ramesh Sharma"
              />
            </Field>
            <Field label="Email Address" error={errors.email}>
              <StableInput
                value={form.email} hasError={!!errors.email}
                onCommit={v => set("email", v)}
                type="email" placeholder="you@example.com"
              />
            </Field>
            <Field label="City / Location" error={errors.city}>
              <StableInput
                value={form.city} hasError={!!errors.city}
                onCommit={v => set("city", v)}
                type="text" placeholder="e.g. Bengaluru"
              />
            </Field>
            <Field label="Doctor Name" error={errors.doctor_name}>
              <StableInput
                value={form.doctor_name} hasError={!!errors.doctor_name}
                onCommit={v => set("doctor_name", v)}
                type="text" placeholder="e.g. Dr. Ankit"
              />
            </Field>
            <Field label="Programme">
              <StableSelect value={form.program_id} onChange={v => set("program_id", v)}>
                {PROGRAMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </StableSelect>
            </Field>
          </div>
        )}

        {screen === 2 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Basic Profile</h2>
            <p className="inq-screen-sub">Age and gender for personalised targets.</p>
            <div className="inq-row">
              <Field label="Age" error={errors.age}>
                <StableInput
                  value={form.age} hasError={!!errors.age}
                  onCommit={v => set("age", v)}
                  type="number" placeholder="45" min="18" max="90"
                />
              </Field>
              <Field label="Gender" error={errors.gender}>
                <StableSelect value={form.gender} hasError={!!errors.gender} onChange={v => set("gender", v)}>
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </StableSelect>
              </Field>
            </div>
          </div>
        )}

        {screen === 3 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Health Snapshot</h2>
            <p className="inq-screen-sub">Body metrics for accurate calorie and macro targets.</p>
            <div className="inq-row">
              <Field label="Height (cm)" error={errors.height}>
                <StableInput
                  value={form.height} hasError={!!errors.height}
                  onCommit={v => set("height", v)}
                  type="number" placeholder="170" min="100" max="250"
                />
              </Field>
              <Field label="Weight (kg)" error={errors.weight}>
                <StableInput
                  value={form.weight} hasError={!!errors.weight}
                  onCommit={v => set("weight", v)}
                  type="number" placeholder="80" min="30" max="300"
                />
              </Field>
            </div>
            <Field label="Diabetes diagnosed since" error={errors.diab_duration}>
              <StableSelect
                value={form.diab_duration} hasError={!!errors.diab_duration}
                onChange={v => set("diab_duration", v)}
              >
                <option value="">Select duration</option>
                <option value="Less than 1 year">Less than 1 year</option>
                <option value="1–3 years">1–3 years</option>
                <option value="3–5 years">3–5 years</option>
                <option value="5–10 years">5–10 years</option>
                <option value="More than 10 years">More than 10 years</option>
              </StableSelect>
            </Field>
            <Field label="Current Medications" optional>
              <StableTextarea
                value={form.medications}
                onCommit={v => set("medications", v)}
                placeholder="e.g. Metformin 500mg…"
              />
            </Field>
            <Field label="Latest HbA1c (%)" optional>
              <StableInput
                value={form.hba1c}
                onCommit={v => set("hba1c", v)}
                type="number" placeholder="e.g. 7.8" step="0.1" min="4" max="20"
              />
            </Field>

            <div className="inq-field-group">
              <label className="inq-label">
                Any existing health conditions? <span className="inq-req">*</span>
              </label>
              <div className="inq-condition-tags">
                {[
                  "Hypertension", "Heart Disease", "Thyroid Disorder",
                  "PCOS / PCOD", "Obesity", "Dyslipidemia", "None of the above",
                ].map((cond) => {
                  const isNone     = cond === "None of the above";
                  const isSelected = form.conditions.includes(cond);
                  return (
                    <button
                      key={cond}
                      type="button"
                      className={`inq-condition-tag${isSelected ? " selected" : ""}`}
                      onClick={() => {
                        setForm(f => {
                          if (isNone)
                            return { ...f, conditions: isSelected ? [] : ["None of the above"] };
                          const without = f.conditions.filter(c => c !== "None of the above");
                          return {
                            ...f,
                            conditions: isSelected
                              ? without.filter(c => c !== cond)
                              : [...without, cond],
                          };
                        });
                      }}
                    >
                      {cond}
                    </button>
                  );
                })}
              </div>
              {errors.conditions && <div className="inq-error">{errors.conditions}</div>}
            </div>
          </div>
        )}

        {screen === 4 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Risk Screening</h2>
            <p className="inq-screen-sub">Safety questions for programme intensity.</p>
            <div className="inq-info-tip">⚠️ Please answer honestly — patient safety is our top priority.</div>
            {[
              { key: "insulin",       q: <>Currently on <strong>insulin</strong>?</> },
              { key: "hypoglycemia",  q: <>History of <strong>hypoglycemia</strong>?</> },
              { key: "kidney",        q: <>Known <strong>kidney disease</strong> (CKD)?</> },
              { key: "complications", q: <><strong>Advanced complications</strong> (neuropathy, retinopathy)?</> },
            ].map(({ key, q }) => (
              <div key={key} className="inq-risk-item">
                <div className="inq-risk-q">{q}</div>
                <div className="inq-risk-toggle">
                  <button
                    className={`inq-risk-opt yes${form.risks[key] ? " selected" : ""}`}
                    onClick={() => setRisk(key, true)}
                  >Yes</button>
                  <button
                    className={`inq-risk-opt no${!form.risks[key] ? " selected" : ""}`}
                    onClick={() => setRisk(key, false)}
                  >No</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {screen === 5 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Lifestyle</h2>
            <p className="inq-screen-sub">Activity and eating patterns for calorie targets.</p>

            <div className="inq-field-group">
              <label className="inq-label">Activity Level</label>
              <div className="inq-activity-grid">
                {[
                  { v: "sedentary", icon: "🛋️", label: "Sedentary", desc: "Desk job, little movement" },
                  { v: "moderate",  icon: "🚶", label: "Moderate",  desc: "Light walks, 3×/week" },
                  { v: "active",    icon: "🏃", label: "Active",    desc: "Regular gym / sport" },
                ].map(a => (
                  <div
                    key={a.v}
                    className={`inq-activity-card${form.activity === a.v ? " selected" : ""}`}
                    onClick={() => { set("activity", a.v); setErrors(e => ({ ...e, activity: undefined })); }}
                  >
                    <span className="inq-act-icon">{a.icon}</span>
                    <span className="inq-act-name">{a.label}</span>
                    <span className="inq-act-desc">{a.desc}</span>
                  </div>
                ))}
              </div>
              {errors.activity && <div className="inq-error">{errors.activity}</div>}
            </div>

            <Field label="Sleep Duration">
              <StableSelect value={form.sleep_duration} onChange={v => set("sleep_duration", v)}>
                <option value="">Select</option>
                <option value="Less than 5 hours">Less than 5 hours</option>
                <option value="5–6 hours">5–6 hours</option>
                <option value="7–8 hours (optimal)">7–8 hours (optimal)</option>
                <option value="More than 8 hours">More than 8 hours</option>
              </StableSelect>
            </Field>

            <div className="inq-field-group">
              <label className="inq-label">Sleep Quality</label>
              <div className="inq-toggle-group">
                {[
                  { v: "Poor", icon: "😴" }, { v: "Fair", icon: "😐" },
                  { v: "Good", icon: "😊" }, { v: "Excellent", icon: "🌟" },
                ].map(s => (
                  <button
                    key={s.v}
                    className={`inq-toggle-btn${form.sleep_quality === s.v ? " selected" : ""}`}
                    onClick={() => set("sleep_quality", s.v)}
                  >
                    <span className="inq-toggle-icon">{s.icon}</span>{s.v}
                  </button>
                ))}
              </div>
            </div>

            <div className="inq-field-group">
              <label className="inq-label">Eating Pattern</label>
              <div className="inq-eating-opts">
                {[
                  { v: "3 Structured Meals",  title: "3 Structured Meals",  desc: "Breakfast, lunch, dinner — no snacking" },
                  { v: "Meals + Snacks",       title: "Meals + Snacks",       desc: "3 meals with 1–2 snacks" },
                  { v: "Grazing / Irregular",  title: "Grazing / Irregular",  desc: "Frequent small portions" },
                  { v: "Intermittent Fasting", title: "Intermittent Fasting", desc: "Defined eating window (e.g. 16:8)" },
                ].map(opt => (
                  <div
                    key={opt.v}
                    className={`inq-eating-opt${form.eating_pattern === opt.v ? " selected" : ""}`}
                    onClick={() => set("eating_pattern", opt.v)}
                  >
                    <div>
                      <div className="inq-eat-title">{opt.title}</div>
                      <div className="inq-eat-desc">{opt.desc}</div>
                    </div>
                    <span className="inq-eat-check">{form.eating_pattern === opt.v ? "✓" : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {screen === 6 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Diet Preferences</h2>
            <p className="inq-screen-sub">Optional — all fields can be skipped.</p>
            <Field label="Food you enjoy" optional>
              <StableTextarea
                value={form.food_likes}
                onCommit={v => set("food_likes", v)}
                placeholder="e.g. dal, sabzi, roti…"
              />
            </Field>
            <Field label="Food you dislike or cannot eat" optional>
              <StableTextarea
                value={form.food_dislikes}
                onCommit={v => set("food_dislikes", v)}
                placeholder="e.g. eggs, spicy food…"
              />
            </Field>
            <Field label="Preferred meal timing" optional>
              <StableInput
                value={form.meal_timing}
                onCommit={v => set("meal_timing", v)}
                type="text" placeholder="e.g. 8am, 1pm, 8pm"
              />
            </Field>
          </div>
        )}

        {screen === 7 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Body Measurements</h2>
            <p className="inq-screen-sub">Optional — for body composition analysis.</p>
            <div className="inq-row">
              <Field label="Waist (cm)" optional>
                <StableInput value={form.waist} onCommit={v => set("waist", v)} type="number" placeholder="90" />
              </Field>
              <Field label="Hips (cm)" optional>
                <StableInput value={form.hips} onCommit={v => set("hips", v)} type="number" placeholder="100" />
              </Field>
            </div>
            <div className="inq-row">
              <Field label="Chest (cm)" optional>
                <StableInput value={form.chest} onCommit={v => set("chest", v)} type="number" placeholder="95" />
              </Field>
              <Field label="Wrist (cm)" optional>
                <StableInput value={form.wrist} onCommit={v => set("wrist", v)} type="number" placeholder="17" />
              </Field>
            </div>
          </div>
        )}

        {screen === 8 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Final Check</h2>
            <p className="inq-screen-sub">Confirm programme, then review all data before submitting.</p>
            <Field label="Programme">
              <StableSelect value={form.program_id} onChange={v => set("program_id", v)}>
                {PROGRAMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </StableSelect>
            </Field>
            {calc.bmi && (
              <div className="inq-calc-preview">
                <div className="inq-calc-title">📊 Calculated Metrics Preview</div>
                <div className="inq-calc-grid">
                  <div className="inq-calc-item"><span>BMI</span><strong>{calc.bmi}</strong></div>
                  <div className="inq-calc-item"><span>Category</span><strong>{calc.bmi_category}</strong></div>
                  <div className="inq-calc-item"><span>Target Cal</span><strong>{calc.target_calories} kcal</strong></div>
                  <div className="inq-calc-item"><span>Protein</span><strong>{calc.protein_g}g</strong></div>
                  <div className="inq-calc-item"><span>Carbs</span><strong>{calc.carbs_g}g</strong></div>
                  <div className="inq-calc-item"><span>Fat</span><strong>{calc.fat_g}g</strong></div>
                  <div className="inq-calc-item"><span>Goal</span><strong>{calc.goal}</strong></div>
                  <div className="inq-calc-item"><span>Body Type</span><strong>{calc.body_category}</strong></div>
                </div>
              </div>
            )}
            <div className="inq-info-tip" style={{ marginTop: 16 }}>
              📋 Next you'll see a full summary of all entered data. You can edit any section before submitting.
            </div>
          </div>
        )}

        {screen === 9 && (
          <div className="inq-screen">
            <h2 className="inq-screen-title">Review & Confirm</h2>
            <p className="inq-screen-sub">
              All data entered so far. Click <strong>Edit</strong> on any section to change, then submit when ready.
            </p>

            <ReviewSection title="Contact & Programme" icon="👤" editStep={1} onEdit={goTo} fields={[
              { label: "Phone",     value: `+${form.country_code} ${form.phone_no}` },
              { label: "Name",      value: form.name },
              { label: "Email",     value: form.email },
              { label: "City",      value: form.city },
              { label: "Doctor",    value: form.doctor_name },
              { label: "Programme", value: PROGRAMS.find(p => p.value === form.program_id)?.label },
            ]} />

            <ReviewSection title="Basic Profile" icon="📋" editStep={2} onEdit={goTo} fields={[
              { label: "Age",    value: form.age    ? `${form.age} years` : "" },
              { label: "Gender", value: form.gender ? form.gender.charAt(0).toUpperCase() + form.gender.slice(1) : "" },
            ]} />

            <ReviewSection title="Health Snapshot" icon="🩺" editStep={3} onEdit={goTo} fields={[
              { label: "Height",         value: form.height       ? `${form.height} cm` : "" },
              { label: "Weight",         value: form.weight       ? `${form.weight} kg` : "" },
              { label: "BMI",            value: calc.bmi          ? `${calc.bmi} (${calc.bmi_category})` : "" },
              { label: "Diabetes since", value: form.diab_duration },
              { label: "HbA1c",          value: form.hba1c        ? `${form.hba1c}%` : "Not provided" },
              { label: "Medications",    value: form.medications  || "Not provided" },
              { label: "Conditions", value: form.conditions.join(", ") || "Not provided" },
            ]} />

            <ReviewSection title="Risk Factors" icon="⚠️" editStep={4} onEdit={goTo} fields={[
              { label: "On Insulin",             value: form.risks.insulin       ? "Yes" : "No" },
              { label: "Hypoglycemia history",   value: form.risks.hypoglycemia  ? "Yes" : "No" },
              { label: "Kidney disease",         value: form.risks.kidney        ? "Yes" : "No" },
              { label: "Advanced complications", value: form.risks.complications ? "Yes" : "No" },
            ]} />

            <ReviewSection title="Lifestyle" icon="🏃" editStep={5} onEdit={goTo} fields={[
              { label: "Activity",       value: form.activity       ? form.activity.charAt(0).toUpperCase() + form.activity.slice(1) : "" },
              { label: "Sleep",          value: form.sleep_duration ? `${form.sleep_duration}${form.sleep_quality ? " · " + form.sleep_quality : ""}` : "" },
              { label: "Eating pattern", value: form.eating_pattern },
            ]} />

            <ReviewSection title="Diet Preferences" icon="🥗" editStep={6} onEdit={goTo} fields={[
              { label: "Food likes",    value: form.food_likes    || "Not provided" },
              { label: "Food dislikes", value: form.food_dislikes || "Not provided" },
              { label: "Meal timing",   value: form.meal_timing   || "Not provided" },
            ]} />

            {(form.waist || form.hips || form.chest || form.wrist) && (
              <ReviewSection title="Body Measurements" icon="📏" editStep={7} onEdit={goTo} fields={[
                { label: "Waist", value: form.waist ? `${form.waist} cm` : "—" },
                { label: "Hips",  value: form.hips  ? `${form.hips} cm`  : "—" },
                { label: "Chest", value: form.chest ? `${form.chest} cm` : "—" },
                { label: "Wrist", value: form.wrist ? `${form.wrist} cm` : "—" },
              ]} />
            )}

            {calc.target_calories && (
              <div className="inq-rv-section inq-rv-calc">
                <div className="inq-rv-section-header">
                  <span className="inq-rv-section-title">📊 Calculated Targets</span>
                </div>
                <div className="inq-calc-grid" style={{ padding: "16px 16px 12px" }}>
                  <div className="inq-calc-item"><span>BMI</span><strong>{calc.bmi}</strong></div>
                  <div className="inq-calc-item"><span>Target Cal</span><strong>{calc.target_calories} kcal</strong></div>
                  <div className="inq-calc-item"><span>Protein</span><strong>{calc.protein_g}g</strong></div>
                  <div className="inq-calc-item"><span>Carbs</span><strong>{calc.carbs_g}g</strong></div>
                  <div className="inq-calc-item"><span>Fat</span><strong>{calc.fat_g}g</strong></div>
                  <div className="inq-calc-item"><span>Goal</span><strong>{calc.goal}</strong></div>
                </div>
              </div>
            )}

            {submitErr && <div className="inq-error" style={{ marginTop: 16, marginBottom: 8 }}>{submitErr}</div>}

            <div className="inq-rv-submit-area">
              <button className="inq-btn-primary" onClick={submitForm} disabled={submitting}>
                {submitting ? "Submitting…" : "✅ Confirm & Submit"}
                {!submitting && <span className="inq-arrow">→</span>}
              </button>
              <button className="inq-btn-secondary" style={{ marginTop: 12 }} onClick={() => goTo(8)}>
                ← Back to edit
              </button>
            </div>
          </div>
        )}

        {screen === 10 && (
          <div className="inq-screen inq-success">

            {submitResult?.reason === "already_fully_registered" && (
              <>
                <div className="inq-success-circle inq-result-warn">⚠️</div>
                <h2 className="inq-success-title">Already Registered</h2>
                <p className="inq-success-sub">
                  <strong>{form.name || "This patient"}</strong> is already fully registered in our system.
                  No changes were made to their existing profile or programme.
                </p>
                <div className="inq-result-badge inq-result-badge--warn">
                  <span>Existing Patient</span>
                  <strong>{refNum}</strong>
                </div>
                <div className="inq-result-info-box">
                  <div className="inq-result-info-row">
                    <span>👤 Profile</span><strong>Already exists — not overwritten</strong>
                  </div>
                  <div className="inq-result-info-row">
                    <span>📋 Programme</span><strong>Already enrolled — no new entry</strong>
                  </div>
                </div>
                <p className="inq-result-hint">
                  If you need to update this patient's details, please contact the care team.
                </p>
              </>
            )}

            {submitResult?.reason === "ncd_only_registration" && (
              <>
                <div className="inq-success-circle inq-result-partial">🔄</div>
                <h2 className="inq-success-title">Programme Added!</h2>
                <p className="inq-success-sub">
                  <strong>{form.name}</strong> was already in our system. A new{" "}
                  <strong>{PROGRAMS.find(p => p.value === form.program_id)?.label}</strong> programme
                  entry has been created under <strong>{form.doctor_name}</strong>.
                </p>
                <div className="inq-result-badge inq-result-badge--partial">
                  <span>Programme Reference</span>
                  <strong>{refNum}</strong>
                </div>
                <div className="inq-result-info-box">
                  <div className="inq-result-info-row">
                    <span>👤 Profile</span><strong>Existing — not overwritten</strong>
                  </div>
                  <div className="inq-result-info-row">
                    <span>📋 Programme</span><strong>✅ New entry created</strong>
                  </div>
                </div>
              </>
            )}

            {(submitResult?.reason === "full_registration" || !submitResult?.reason) && (
              <>
                <div className="inq-success-circle">✅</div>
                <h2 className="inq-success-title">Patient Registered!</h2>
                <p className="inq-success-sub">
                  <strong>{form.name}</strong> has been successfully registered in the{" "}
                  <strong>{PROGRAMS.find(p => p.value === form.program_id)?.label}</strong> programme
                  under <strong>{form.doctor_name}</strong>.
                </p>
                <div className="inq-result-badge inq-result-badge--success">
                  <span>Reference Number</span>
                  <strong>{refNum}</strong>
                </div>
                <div className="inq-result-info-box">
                  <div className="inq-result-info-row">
                    <span>👤 Profile</span><strong>✅ New patient created</strong>
                  </div>
                  <div className="inq-result-info-row">
                    <span>📋 Programme</span><strong>✅ Enrolled successfully</strong>
                  </div>
                </div>
                <div className="inq-success-steps">
                  {[
                    { n:1, title:"Doctor's Call",       body:"A FutureHealth physician will review the assessment and call to discuss the personalised plan." },
                    { n:2, title:"Programme Enrolment", body:"Patient receives a welcome kit via WhatsApp / email with diet targets and tracking tools." },
                    { n:3, title:"Regular Check-ins",   body:"Weekly coaching sessions and monthly blood work reviews keep the patient on track safely." },
                  ].map(s => (
                    <div key={s.n} className="inq-ns-item">
                      <div className="inq-ns-num">{s.n}</div>
                      <div>
                        <div className="inq-ns-title">{s.title}</div>
                        <div className="inq-ns-body">{s.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <button className="inq-btn-secondary" style={{ marginTop: 24 }}
              onClick={() => navigate("/care-coordinator")}>
              ← Back to Home
            </button>
          </div>
        )}

      </div>

      {isFormStep && (
        <div className="inq-sticky-footer">
          <div className="inq-footer-inner">
            <button className="inq-btn-back" onClick={back}>← Back</button>
            <button className="inq-btn-primary inq-btn-flex" onClick={next} disabled={submitting}>
              {screen === 8 ? "Review Assessment" : "Continue"}
              <span className="inq-arrow">→</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}